/**
 * usePortfolioOpportunitiesWithFilter Hook
 * 
 * Fetches and manages portfolio and opportunities data with filtering.
 * Isolates API calls from UI components per Rule 6.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '../shared/utils/api';
import { Portfolio } from '../shared/types/portfolio';
import { Opportunity, OpportunityStatus } from '../shared/types/opportunity';
import { logger } from '../shared/utils/logger';

export type OpportunityFilter = 'all' | OpportunityStatus;

interface UsePortfolioOpportunitiesResult {
  portfolio: Portfolio | null;
  opportunities: Opportunity[];
  filteredOpportunities: Opportunity[];
  filterCounts: Record<OpportunityFilter, number>;
  filter: OpportunityFilter;
  setFilter: (filter: OpportunityFilter) => void;
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
  error: boolean;
}

export function usePortfolioOpportunitiesWithFilter(
  portfolioId: number | null
): UsePortfolioOpportunitiesResult {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<OpportunityFilter>('all');
  const [error, setError] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!portfolioId || portfolioId <= 0 || isNaN(portfolioId)) {
      logger.warn('Invalid portfolio ID', 'opportunities', { portfolioId, type: typeof portfolioId });
      setLoading(false);
      setError(true);
      return;
    }

    try {
      setError(false);
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      logger.debug('Fetching portfolio and opportunities', 'opportunities', { portfolioId });
      
      // Fetch portfolio and opportunities in parallel
      let portfolioRes: any;
      let opportunitiesRes: any;
      
      try {
        portfolioRes = await authApi.getPortfolio(portfolioId);
        logger.debug('Raw portfolio response', 'opportunities', { 
          portfolioId, 
          success: portfolioRes?.success,
          hasPortfolio: !!portfolioRes?.portfolio,
          keys: portfolioRes ? Object.keys(portfolioRes) : [],
        });
      } catch (portfolioErr) {
        logger.error('Portfolio API call failed', portfolioErr instanceof Error ? portfolioErr : undefined, 'opportunities', { portfolioId });
        setError(true);
        setPortfolio(null);
        return;
      }
      
      try {
        opportunitiesRes = await authApi.getOpportunities(portfolioId);
        logger.debug('Raw opportunities response', 'opportunities', { 
          portfolioId,
          success: opportunitiesRes?.success,
          count: opportunitiesRes?.opportunities?.length,
        });
      } catch (oppErr) {
        logger.warn('Opportunities API call failed', 'opportunities', { portfolioId, error: oppErr instanceof Error ? oppErr.message : String(oppErr) });
        opportunitiesRes = { success: true, opportunities: [] };
      }
      
      // Check portfolio response
      if (!portfolioRes || !portfolioRes.success || !portfolioRes.portfolio) {
        logger.error('Portfolio response invalid', undefined, 'opportunities', { 
          portfolioId, 
          response: JSON.stringify(portfolioRes).slice(0, 200),
        });
        setError(true);
        setPortfolio(null);
        return;
      }
      
      logger.debug('Portfolio fetched successfully', 'opportunities', { 
        portfolioId, 
        portfolioName: portfolioRes.portfolio?.name 
      });
      
      setPortfolio(portfolioRes.portfolio || null);
      setOpportunities(opportunitiesRes.opportunities || []);
      
    } catch (err: unknown) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      logger.error('Failed to fetch portfolio opportunities', errorObj, 'opportunities', { portfolioId });
      setError(true);
      setPortfolio(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => fetchData(true), [fetchData]);

  // Filter opportunities
  const filteredOpportunities = useMemo(() => {
    if (filter === 'all') return opportunities;
    return opportunities.filter(o => o.status === filter);
  }, [opportunities, filter]);

  // Get filter counts for new statuses
  const filterCounts = useMemo(() => {
    const counts: Record<OpportunityFilter, number> = {
      all: opportunities.length,
      evaluating: 0,
      under_contract: 0,
      owned: 0,
      selling: 0,
      sold: 0,
      passed: 0,
    };
    opportunities.forEach(o => {
      if (o.status in counts) {
        counts[o.status as OpportunityStatus]++;
      }
    });
    return counts;
  }, [opportunities]);

  return {
    portfolio,
    opportunities,
    filteredOpportunities,
    filterCounts,
    filter,
    setFilter,
    loading,
    refreshing,
    refresh,
    error,
  };
}
